/**
 * Copy/paste nodes between flows in FlowDesigner.
 *
 * @module useClipboard
 */
import { useCallback, useState } from 'react';
import type { WorkflowNode, WorkflowEdge } from '../../../../types/workflow';
import {
  serializeNodeData,
  isValidClipboardPayload,
  topologicalSort,
  type ClipboardPayload,
} from '../clipboard-utils';
import { isTempNodeId } from '../utils';
import {
  buildNodeQuickCreateHandlers,
  type WorkflowNodeType,
  type QuickCreateTarget,
  type BranchType,
} from '../helpers';
import type { UndoHistoryHandle } from './useUndoHistory';

export interface PendingBranchPaste {
  payload: ClipboardPayload;
  selectedNodeId: string;
  outgoingEdges: WorkflowEdge[];
}

interface UseClipboardParams {
  workflowId: string | null;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  getNodes: () => WorkflowNode[];
  setNodes: (nodes: WorkflowNode[] | ((prev: WorkflowNode[]) => WorkflowNode[])) => void;
  setEdges: (edges: WorkflowEdge[] | ((prev: WorkflowEdge[]) => WorkflowEdge[])) => void;
  selectedNodeIds: ReadonlySet<string>;
  selectedNodeForConfig: WorkflowNode | null;
  ensureWorkflow: () => Promise<string>;
  createNodeMutation: any;
  createEdgeMutation: any;
  deleteEdgeMutation: any;
  workflowDetailRefetch: () => void;
  toast: {
    success: (msg: string) => void;
    error: (msg: string, options?: { reportError?: boolean }) => void;
    info: (msg: string) => void;
  };
  undoHistory: UndoHistoryHandle;
  handleNodeConfig: (node: WorkflowNode) => void;
  handleQuickCreateNode: (sourceNodeId: string, nodeType: QuickCreateTarget) => void;
  handleBranchQuickCreate: (
    sourceNodeId: string,
    nodeType: QuickCreateTarget,
    branchType: BranchType
  ) => void;
  hasApiCatalog: boolean;
  hasSandboxActions: boolean;
  hasMcp: boolean;
  setCachePopover: (popover: { nodeId: string; position: { x: number; y: number } } | null) => void;
  setIsPlaybookOpen: (open: boolean) => void;
  setPlaybookScrollToNodeId: (nodeId: string | null) => void;
}

function attachNodeHandlers(
  node: WorkflowNode,
  handlers: {
    handleNodeConfig: (node: WorkflowNode) => void;
    handleQuickCreateNode: (sourceNodeId: string, nodeType: QuickCreateTarget) => void;
    handleBranchQuickCreate: (
      sourceNodeId: string,
      nodeType: QuickCreateTarget,
      branchType: BranchType
    ) => void;
    handleCacheClick: (nodeId: string, event: React.MouseEvent) => void;
    handlePlaybookClick: (nodeId?: string) => void;
    hasApiCatalog: boolean;
    hasSandboxActions: boolean;
    hasMcp: boolean;
  }
): Record<string, any> {
  const nodeData = node.data as Record<string, unknown> | undefined;
  const playbookSteps = Array.isArray(nodeData?.playbookSteps) ? nodeData.playbookSteps : undefined;
  return {
    ...node.data,
    isPersisted: true,
    onConfigClick: () => handlers.handleNodeConfig(node),
    onCacheClick: (event: React.MouseEvent) => handlers.handleCacheClick(node.id, event),
    playbookStepCount: playbookSteps ? playbookSteps.length : undefined,
    onPlaybookClick: () => handlers.handlePlaybookClick(node.id),
    ...buildNodeQuickCreateHandlers({
      nodeType: node.type as WorkflowNodeType,
      nodeId: node.id,
      allowTemp: true,
      handleQuickCreate: handlers.handleQuickCreateNode,
      handleBranchCreate: handlers.handleBranchQuickCreate,
      enableApiSequence: handlers.hasApiCatalog,
      enableSandboxAction: handlers.hasSandboxActions,
      enableMcp: handlers.hasMcp,
    }),
    ...(node.type === 'condition' ? { enableApiSequence: handlers.hasApiCatalog } : {}),
  };
}

export function useClipboard({
  workflowId: _workflowId,
  nodes,
  edges,
  getNodes,
  setNodes,
  setEdges,
  selectedNodeIds,
  selectedNodeForConfig,
  ensureWorkflow,
  createNodeMutation,
  createEdgeMutation,
  deleteEdgeMutation,
  workflowDetailRefetch,
  toast,
  undoHistory,
  handleNodeConfig,
  handleQuickCreateNode,
  handleBranchQuickCreate,
  hasApiCatalog,
  hasSandboxActions,
  hasMcp,
  setCachePopover,
  setIsPlaybookOpen,
  setPlaybookScrollToNodeId,
}: UseClipboardParams) {
  const [isPasting, setIsPasting] = useState(false);
  const [pendingBranchPaste, setPendingBranchPaste] = useState<PendingBranchPaste | null>(null);

  // ── Copy ──────────────────────────────────────────────────────────
  const handleCopy = useCallback(async () => {
    // Determine which nodes to copy
    let nodeIdsToCopy: string[];
    if (selectedNodeIds.size > 1) {
      nodeIdsToCopy = [...selectedNodeIds].filter((id) => !isTempNodeId(id));
    } else if (selectedNodeForConfig && !isTempNodeId(selectedNodeForConfig.id)) {
      nodeIdsToCopy = [selectedNodeForConfig.id];
    } else {
      toast.info('No nodes selected to copy');
      return;
    }

    if (nodeIdsToCopy.length === 0) {
      toast.info('No persisted nodes to copy');
      return;
    }

    // Find internal edges
    const nodeIdSet = new Set(nodeIdsToCopy);
    const internalEdges = edges.filter((e) => nodeIdSet.has(e.source) && nodeIdSet.has(e.target));

    // Sort topologically
    const sorted = topologicalSort(nodeIdsToCopy, edges);

    // Build the payload
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    const payload: ClipboardPayload = {
      version: 1,
      source: 'chatsdet-flow-designer',
      nodes: sorted
        .map((id) => nodeMap.get(id))
        .filter((n): n is WorkflowNode => n !== null && n !== undefined)
        .map((n) => ({
          originalId: n.id,
          type: n.type ?? 'action',
          data: serializeNodeData(n.data as Record<string, unknown>),
        })),
      internalEdges: internalEdges.map((e) => ({
        originalSource: e.source,
        originalTarget: e.target,
        edgeType: e.edgeType ?? e.type ?? 'default',
      })),
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload));
      toast.success(`Copied ${payload.nodes.length} node${payload.nodes.length !== 1 ? 's' : ''}`);
    } catch {
      toast.error('Failed to copy — clipboard permission denied', { reportError: false });
    }
  }, [selectedNodeIds, selectedNodeForConfig, nodes, edges, toast]);

  // ── Paste (internal) ──────────────────────────────────────────────
  const executePaste = useCallback(
    async (
      payload: ClipboardPayload,
      spliceAfterNodeId: string | null,
      branchEdgeType?: string
    ) => {
      setIsPasting(true);
      try {
        const wfId = await ensureWorkflow();
        undoHistory.captureSnapshot('Paste nodes');

        const currentNodes = getNodes();

        // Create nodes sequentially, building oldId → newId map
        const idMap = new Map<string, string>();
        const createdNodes: WorkflowNode[] = [];

        for (let i = 0; i < payload.nodes.length; i++) {
          const copiedNode = payload.nodes[i]!;
          const result = await createNodeMutation.mutateAsync({
            workflowId: wfId,
            node: {
              nodeType: copiedNode.type,
              configJson: copiedNode.data,
              canonicalPageId: (copiedNode.data as Record<string, any>).canonicalPageId,
              canonicalActionId: (copiedNode.data as Record<string, any>).canonicalActionId,
              orderIndex: currentNodes.length + i,
            },
          });

          const newId = result.node.id;
          idMap.set(copiedNode.originalId, newId);

          const newNode: WorkflowNode = {
            id: newId,
            type: copiedNode.type,
            data: copiedNode.data,
          };
          createdNodes.push(newNode);
        }

        // Create internal edges using remapped IDs
        const createdInternalEdges: WorkflowEdge[] = [];
        for (const ie of payload.internalEdges) {
          const newSource = idMap.get(ie.originalSource);
          const newTarget = idMap.get(ie.originalTarget);
          if (!newSource || !newTarget) continue;

          const edgeResult = await createEdgeMutation.mutateAsync({
            workflowId: wfId,
            edge: { sourceNodeId: newSource, targetNodeId: newTarget, edgeType: ie.edgeType },
          });
          createdInternalEdges.push({
            id: edgeResult.edge.id,
            source: newSource,
            target: newTarget,
            type: ie.edgeType,
            edgeType: ie.edgeType,
          });
        }

        // Determine first and last pasted nodes (in topological order)
        const firstPastedId = idMap.get(payload.nodes[0]!.originalId)!;
        const lastPastedId = idMap.get(payload.nodes[payload.nodes.length - 1]!.originalId)!;

        // Splice edges
        const edgesToRemove: string[] = [];
        const edgesToCreate: Array<{
          sourceNodeId: string;
          targetNodeId: string;
          edgeType: string;
        }> = [];

        if (spliceAfterNodeId) {
          // Find outgoing edges from the selected node
          const outgoing = edges.filter((e) => e.source === spliceAfterNodeId);
          const effectiveEdgeType = branchEdgeType ?? 'default';

          if (branchEdgeType) {
            // Paste into a specific branch of a condition node
            const branchEdge = outgoing.find((e) => (e.edgeType ?? e.type) === branchEdgeType);
            if (branchEdge) {
              // Remove the existing branch edge, splice in
              edgesToRemove.push(branchEdge.id);
              edgesToCreate.push({
                sourceNodeId: spliceAfterNodeId,
                targetNodeId: firstPastedId,
                edgeType: effectiveEdgeType,
              });
              edgesToCreate.push({
                sourceNodeId: lastPastedId,
                targetNodeId: branchEdge.target,
                edgeType: 'default',
              });
            } else {
              // No existing edge on this branch — just append
              edgesToCreate.push({
                sourceNodeId: spliceAfterNodeId,
                targetNodeId: firstPastedId,
                edgeType: effectiveEdgeType,
              });
            }
          } else if (outgoing.length === 1) {
            // Single outgoing: splice
            const existingEdge = outgoing[0]!;
            edgesToRemove.push(existingEdge.id);
            edgesToCreate.push({
              sourceNodeId: spliceAfterNodeId,
              targetNodeId: firstPastedId,
              edgeType: existingEdge.edgeType ?? existingEdge.type ?? 'default',
            });
            edgesToCreate.push({
              sourceNodeId: lastPastedId,
              targetNodeId: existingEdge.target,
              edgeType: 'default',
            });
          } else if (outgoing.length === 0) {
            // No outgoing: append after
            edgesToCreate.push({
              sourceNodeId: spliceAfterNodeId,
              targetNodeId: firstPastedId,
              edgeType: 'default',
            });
          } else {
            // Multiple outgoing (condition node) — should have been handled by branch selection
            // Default: append after without removing anything
            edgesToCreate.push({
              sourceNodeId: spliceAfterNodeId,
              targetNodeId: firstPastedId,
              edgeType: 'default',
            });
          }
        }

        // Delete old edges
        for (const edgeId of edgesToRemove) {
          await deleteEdgeMutation.mutateAsync(edgeId);
        }

        // Create splice edges
        const newSpliceEdges: WorkflowEdge[] = [];
        for (const ec of edgesToCreate) {
          const edgeResult = await createEdgeMutation.mutateAsync({
            workflowId: wfId,
            edge: ec,
          });
          newSpliceEdges.push({
            id: edgeResult.edge.id,
            source: ec.sourceNodeId,
            target: ec.targetNodeId,
            type: ec.edgeType,
            edgeType: ec.edgeType,
          });
        }

        // Rehydrate handlers on created nodes and update canvas
        const handlersConfig = {
          handleNodeConfig,
          handleQuickCreateNode,
          handleBranchQuickCreate,
          handleCacheClick: (nodeId: string, event: React.MouseEvent) => {
            setCachePopover({ nodeId, position: { x: event.clientX, y: event.clientY } });
          },
          handlePlaybookClick: (nodeId?: string) => {
            if (nodeId) setPlaybookScrollToNodeId(nodeId);
            setIsPlaybookOpen(true);
          },
          hasApiCatalog,
          hasSandboxActions,
          hasMcp,
        };

        const rehydratedNodes = createdNodes.map((n) => ({
          ...n,
          data: attachNodeHandlers(n, handlersConfig),
        }));

        setNodes((nds) => [...nds, ...rehydratedNodes]);
        setEdges((eds) => {
          const removed = new Set(edgesToRemove);
          return [
            ...eds.filter((e) => !removed.has(e.id)),
            ...createdInternalEdges,
            ...newSpliceEdges,
          ];
        });

        toast.success(
          `Pasted ${payload.nodes.length} node${payload.nodes.length !== 1 ? 's' : ''}`
        );
      } catch {
        toast.error('Failed to paste nodes');
        workflowDetailRefetch();
      } finally {
        setIsPasting(false);
      }
    },
    [
      ensureWorkflow,
      undoHistory,
      getNodes,
      edges,
      createNodeMutation,
      createEdgeMutation,
      deleteEdgeMutation,
      setNodes,
      setEdges,
      toast,
      workflowDetailRefetch,
      handleNodeConfig,
      handleQuickCreateNode,
      handleBranchQuickCreate,
      hasApiCatalog,
      hasSandboxActions,
      hasMcp,
      setCachePopover,
      setIsPlaybookOpen,
    ]
  );

  // ── Paste (entry point) ───────────────────────────────────────────
  const handlePaste = useCallback(async () => {
    if (isPasting) return;

    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      toast.error('Failed to read clipboard — permission denied', { reportError: false });
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      // Not JSON — silently ignore (user may be pasting text elsewhere)
      return;
    }

    if (!isValidClipboardPayload(parsed)) return;
    const payload = parsed;

    // Determine insertion point
    const selectedId = selectedNodeForConfig?.id ?? null;
    const spliceAfterNodeId = selectedId && !isTempNodeId(selectedId) ? selectedId : null;

    if (spliceAfterNodeId) {
      // Check if it's a condition node with multiple outgoing edges
      const outgoing = edges.filter((e) => e.source === spliceAfterNodeId);
      const selectedNode = nodes.find((n) => n.id === spliceAfterNodeId);
      if (selectedNode?.type === 'condition' && outgoing.length > 1) {
        // Prompt user to pick a branch
        setPendingBranchPaste({
          payload,
          selectedNodeId: spliceAfterNodeId,
          outgoingEdges: outgoing,
        });
        return;
      }
    }

    await executePaste(payload, spliceAfterNodeId);
  }, [isPasting, selectedNodeForConfig, edges, nodes, toast, executePaste]);

  // ── Branch selection callbacks ────────────────────────────────────
  const confirmBranchPaste = useCallback(
    async (edgeType: string) => {
      if (!pendingBranchPaste) return;
      const { payload, selectedNodeId } = pendingBranchPaste;
      setPendingBranchPaste(null);
      await executePaste(payload, selectedNodeId, edgeType);
    },
    [pendingBranchPaste, executePaste]
  );

  const cancelBranchPaste = useCallback(() => {
    setPendingBranchPaste(null);
  }, []);

  return {
    handleCopy,
    handlePaste,
    isPasting,
    pendingBranchPaste,
    confirmBranchPaste,
    cancelBranchPaste,
  };
}
